/**
 * GT Product Options Component JavaScript
 * 功能：处理产品定制选项的交互、验证和表单提交
 * 作者：GT Team
 * 创建时间：2024-12-19
 * 最后修改：2024-12-19
 */

class GTProductOptions {
  constructor() {
    this.container = document.querySelector('[data-product-options]');
    this.optionGroups = document.querySelectorAll('.gt-option-group');
    this.form = this.container?.closest('form');
    
    if (!this.container) {
      return;
    }
    
    if (!this.form) {
      // 尝试查找产品表单
      this.form = document.querySelector('form[action*="/cart/add"]') || 
                  document.querySelector('product-form form') ||
                  document.querySelector('form[data-product-form]');
      
      if (!this.form) {
        return;
      }
    }
    
    // 获取组件设置
    this.enableValidation = this.container.dataset.enableValidation !== 'false';
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.setupFormValidation();
    this.updateHiddenFields();
    
    // 确保组件初始状态为可用
    this.container.classList.remove('disabled');
    this.container.querySelectorAll('.gt-option-input').forEach(input => {
      input.disabled = false;
    });
  }
  
  bindEvents() {
    // 监听选项点击
    this.container.addEventListener('click', (e) => {
      // 检查是否点击了标签
      if (e.target.matches('.gt-option-label')) {
        const input = e.target.previousElementSibling;
        if (input && input.matches('.gt-option-input')) {
          input.checked = true;
          this.handleOptionChange(input);
        }
      }
      
      // 检查是否点击了整个选项按钮区域
      if (e.target.matches('.gt-option-pill')) {
        const input = e.target.querySelector('.gt-option-input');
        const label = e.target.querySelector('.gt-option-label');
        if (input && input.matches('.gt-option-input')) {
          input.checked = true;
          this.handleOptionChange(input);
        }
      }
      
      // Info按钮的点击事件现在由模态框组件自己处理
    });
    
    // 监听选项变化
    this.container.addEventListener('change', (e) => {
      if (e.target.matches('.gt-option-input')) {
        this.handleOptionChange(e.target);
      }
    });
    
    // 监听表单提交
    this.form.addEventListener('submit', (e) => {
      // 确保隐藏字段在提交前更新
      this.updateHiddenFields();
      
      if (this.enableValidation && !this.validateOptions()) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });
    
    // 监听键盘导航
    this.container.addEventListener('keydown', (e) => {
      this.handleKeyboardNavigation(e);
    });
    
    // 模态框事件现在由独立的模态框组件处理
  }
  
  handleOptionChange(input) {
    const group = input.closest('.gt-option-group');
    const groupName = group.dataset.optionGroup;
    
    // 清除该组的错误状态
    this.clearGroupError(group);
    
    // 更新隐藏字段
    this.updateHiddenFields();
    
    // 触发自定义事件
    this.container.dispatchEvent(new CustomEvent('gt-option-changed', {
      detail: {
        group: groupName,
        value: input.value,
        label: input.dataset.optionLabel,
        id: input.dataset.optionId
      }
    }));
  }
  
  handleKeyboardNavigation(e) {
    const currentInput = e.target;
    if (!currentInput.matches('.gt-option-input')) return;
    
    const group = currentInput.closest('.gt-option-group');
    const inputs = Array.from(group.querySelectorAll('.gt-option-input'));
    const currentIndex = inputs.indexOf(currentInput);
    
    let nextIndex = currentIndex;
    
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : inputs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = currentIndex < inputs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = inputs.length - 1;
        break;
      default:
        return;
    }
    
    if (nextIndex !== currentIndex) {
      inputs[nextIndex].focus();
      inputs[nextIndex].checked = true;
      this.handleOptionChange(inputs[nextIndex]);
    }
  }
  
  validateOptions() {
    let isValid = true;
    
    this.optionGroups.forEach(group => {
      const isRequired = group.dataset.required === 'true';
      const hasSelection = group.querySelector('.gt-option-input:checked');
      
      if (isRequired && !hasSelection) {
        this.showGroupError(group, 'Please select one option.');
        isValid = false;
      } else {
        this.clearGroupError(group);
      }
    });
    
    return isValid;
  }
  
  showGroupError(group, message) {
    const errorElement = group.querySelector('[data-error-message]');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('show');
      errorElement.setAttribute('aria-live', 'polite');
    }
    
    // 添加错误状态类
    group.classList.add('gt-option-group--error');
  }
  
  clearGroupError(group) {
    const errorElement = group.querySelector('[data-error-message]');
    if (errorElement) {
      errorElement.classList.remove('show');
      errorElement.removeAttribute('aria-live');
    }
    
    // 移除错误状态类
    group.classList.remove('gt-option-group--error');
  }
  
  updateHiddenFields() {
    // 隐藏字段ID更新 - 暂时注释掉，目前不需要
    /*
    // 更新西装长度ID
    const blazerInput = this.container.querySelector('input[name="properties[blazer_length]"]:checked');
    const blazerIdField = this.container.querySelector('[data-blazer-length-id]');
    if (blazerInput && blazerIdField && blazerInput.dataset.optionId) {
      blazerIdField.value = `gid://shopify/Metaobject/${blazerInput.dataset.optionId}`;
    }
    
    // 更新裤子版型ID
    const pantInput = this.container.querySelector('input[name="properties[pant_fit]"]:checked');
    const pantIdField = this.container.querySelector('[data-pant-fit-id]');
    if (pantInput && pantIdField && pantInput.dataset.optionId) {
      pantIdField.value = `gid://shopify/Metaobject/${pantInput.dataset.optionId}`;
    }
    
    // 更新衬衫长度ID
    const shirtLengthInput = this.container.querySelector('input[name="properties[shirt_length]"]:checked');
    const shirtLengthIdField = this.container.querySelector('[data-shirt-length-id]');
    if (shirtLengthInput && shirtLengthIdField && shirtLengthInput.dataset.optionId) {
      shirtLengthIdField.value = `gid://shopify/Metaobject/${shirtLengthInput.dataset.optionId}`;
    }
    
    // 更新衬衫版型ID
    const shirtFitInput = this.container.querySelector('input[name="properties[shirt_fit]"]:checked');
    const shirtFitIdField = this.container.querySelector('[data-shirt-fit-id]');
    if (shirtFitInput && shirtFitIdField && shirtFitInput.dataset.optionId) {
      shirtFitIdField.value = `gid://shopify/Metaobject/${shirtFitInput.dataset.optionId}`;
    }
    */
  }
  
  setupFormValidation() {
    // 监听产品表单的变化，检查产品是否可购买
    const productForm = this.form;
    if (productForm) {
      // 监听变体选择器变化
      productForm.addEventListener('change', (e) => {
        if (e.target.matches('input[name="id"]') || e.target.matches('select[name="id"]')) {
          this.updateAvailability();
        }
      });
      
      // 延迟初始检查，确保变体选择器已初始化
      setTimeout(() => {
       //this.updateAvailability();
      }, 100);
    }
  }
  
  updateAvailability() {
    // 检查产品是否可购买
    const variantSelect = this.form.querySelector('select[name="id"]');
    const variantInputs = this.form.querySelectorAll('input[name="id"]');
    
    let isAvailable = true; // 默认为可用
    
    if (variantSelect) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      isAvailable = selectedOption && !selectedOption.disabled;
    } else if (variantInputs.length > 0) {
      const hiddenInput = Array.from(variantInputs).find(input => input.type === 'hidden');
      if (hiddenInput) {
        // Dawn主题使用隐藏的input保存当前variant id，如果为空说明变体不存在
        isAvailable = hiddenInput.value !== '';
      } else {
        // 处理传统的radio类型的变体选择
        const checkedInput = Array.from(variantInputs).find(input => input.checked);
        isAvailable = checkedInput && !checkedInput.disabled;
      }
    } else {
      // 如果没有找到变体选择器，假设产品可用
      isAvailable = true;
    }
    
    // 更新组件可用性
    if (isAvailable) {
      this.container.classList.remove('disabled');
      this.container.querySelectorAll('.gt-option-input').forEach(input => {
        input.disabled = false;
      });
    } else {
      this.container.classList.add('disabled');
      this.container.querySelectorAll('.gt-option-input').forEach(input => {
        input.disabled = true;
      });
    }
  }
  
  // 公共方法：获取选中的选项
  getSelectedOptions() {
    const selected = {};
    
    this.optionGroups.forEach(group => {
      const groupName = group.dataset.optionGroup;
      const checkedInput = group.querySelector('.gt-option-input:checked');
      
      if (checkedInput) {
        selected[groupName] = {
          value: checkedInput.value,
          label: checkedInput.dataset.optionLabel,
          id: checkedInput.dataset.optionId
        };
      }
    });
    
    return selected;
  }
  
  // 公共方法：设置选项
  setOption(groupName, value) {
    const group = this.container.querySelector(`[data-option-group="${groupName}"]`);
    if (!group) return false;
    
    const input = group.querySelector(`input[value="${value}"]`);
    if (!input) return false;
    
    input.checked = true;
    this.handleOptionChange(input);
    return true;
  }
  
}

// 初始化组件
document.addEventListener('DOMContentLoaded', () => {
  new GTProductOptions();
});

// 支持动态加载
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GTProductOptions();
  });
} else {
  new GTProductOptions();
}

// 导出类供外部使用
window.GTProductOptions = GTProductOptions;